import { Injectable } from '@angular/core';
import { MapInfoWindow } from '@angular/google-maps';
import { BehaviorSubject, distinctUntilChanged, Observable, Subject } from 'rxjs';
import { Facultativo, MedicalCenter } from '../models/api';
import { SearchInput } from '../models/components';
import { GoogleMapMarkers } from '../models/google-map';
import { AppState } from '../models/state';
import { capitalize, UiService } from './ui.service';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root'
})
export class GoogleMapService {

  map: google.maps.Map;
  infoWindowOpened: MapInfoWindow;
  infoWindow = new google.maps.InfoWindow();
  infoWindows: google.maps.InfoWindow[];
  mapMarkers: any;
  circle: google.maps.Circle;
  infoWindowHandlers: { [key: string]: (item?: Partial<SearchInput>) => void; };
  readonly defaultRadio: number;
  private radioSubject$: BehaviorSubject<number>;
  private scrollTo$: Subject<number>;
  private mapLoadedSubject$: BehaviorSubject<boolean>;

  constructor(
    private utils: UtilsService,
    private ui: UiService
  ) {
    this.infoWindowHandlers = {};
    this.infoWindows = [];
    this.mapMarkers = [];
    this.defaultRadio = 20;
    this.scrollTo$ = new Subject<number>();
    this.radioSubject$ = new BehaviorSubject<number>(this.defaultRadio);
    this.mapLoadedSubject$ = new BehaviorSubject<boolean>(false);
    this.getRadio().subscribe(radio => {
      if (this.circle) this.circle?.setRadius(radio * 1000);
    });
  }

  addMarkers(
    data: GoogleMapMarkers,
    state: Partial<AppState>,
    setCurrentLocation = null,
    tab = 0,
  ) {
    this.clearInfoWindowHandlers();
    this.clearMarkers();
    const locations = data.locations;
    for (const [index, loc] of locations.entries()) {
      const lat = loc.lat;
      const lng = loc.lng;
      const position: google.maps.LatLngLiteral = { lat, lng };
      const firstPage = tab === 0
        ? state?.centersPage === 0
        : state.doctorsPage === 0;
      const isLastOne = index === locations.length - 1 && !firstPage;
      let icon = data.icon;
      if (index === 0 && locations.length > 1 && firstPage) this.setMedicalCenterAsSelected(state, 0, tab);


      const tempMarker = new google.maps.Marker({
        position, map: this.map, icon: {
          url: 'assets/icons/' + icon
        }
      });

      if (data.showModalInfo) {
        this.setInfoWindowHandler(
          tempMarker,
          index,
          state,
          tab,
          firstPage,
          loc as any,
          isLastOne);
      }

      this.mapMarkers.push(tempMarker);
    }

    if (locations.length > 1 || setCurrentLocation) {
      const tempMarkerCenterLocation = new google.maps.Marker({
        position: data.center, map: this.map, icon: {
          url: 'assets/icons/default-marker.png'
        }
      });

      this.mapMarkers.push(tempMarkerCenterLocation);
    }
    if (data.showModalCircle) this.addMapCircle(data.center);
  }

  currentRadio(): number {
    return this.radioSubject$.getValue();
  }

  updateRadio(radio: number) {
    this.radioSubject$.next(radio);
  }

  mapLoaded() {
    this.mapLoadedSubject$.next(true);
  }

  mapDestroy() {
    this.mapLoadedSubject$.next(false);
  }

  isMapLoaded(): Observable<boolean> {
    return this.mapLoadedSubject$.asObservable()
      .pipe(
        distinctUntilChanged()
      );
  }

  clearInfoWindowHandlers() {
    this.infoWindowHandlers = {};
  }

  clearMarkers() {
    this.mapMarkers.forEach((marker) => {
      marker.setMap(null);
      marker = null;
    });
    this.mapMarkers = [];
  }

  getRadio() {
    return this.radioSubject$.asObservable();
  }

  getLocations(data: MedicalCenter[] | Facultativo[]): google.maps.LatLngLiteral[] {
    return data.map(m => ({
      lat: Number(m.latitud),
      lng: Number(m.longitud)
    }));
  }

  getInfoWindowContent(ele: MedicalCenter & Facultativo, tabIndex: number, state: Partial<AppState> ) {
    return `
    <div class="google-info-window">
      <div class="flex f-wrap uppercase mapfre-mb-s">
        ${this.utils.showOnlineMeeting(ele, state) ? '<div class="chip">CITA ONLINE</div>' : ''}
        ${ele.indMapfreSalud ? '<div class="chip">CENTRO Médico MAPFRE</div>' : ''}
        ${ele.indUrgencias && tabIndex === 0 && state.searchEngineType !== 'emergency-center' ? '<div class="chip">urgencias</div>' : ''}
      </div>
      <div class="mapfre-fs-s dark">${ele.nombreWeb}</div>
      <div class="dark-blue mapfre-fs-xs">${ele?.descripcionTipoVia} ${capitalize(ele?.nombreVia)}
      ${ele?.numeroVia}, ${capitalize(ele?.descripcionPoblacion)} |  ${this.utils.getKmDistance(ele.distancia)} </div>
    </div>`;
  }

  openInfoWindow(item: Partial<SearchInput>) {
    this.closeAllInfoWindows();
    const fn = this.infoWindowHandlers[item?.codigoCentroMedicoMapfre];
    if (typeof fn === 'function') fn(item);
  }

  getSpainCoordinates(): google.maps.LatLng {
    return new google.maps.LatLng(40.0000000, -4.0000000);
  }

  scrollTo(): Observable<number> {
    return this.scrollTo$.asObservable()
      .pipe();
  }

  private closeAllInfoWindows() {
    const closeButtons = document.getElementsByClassName('gm-ui-hover-effect') as any;
    for (let btn of closeButtons) {
      btn?.click();
    }
  }

  private setInfoWindowHandler(
    marker: google.maps.Marker,
    index: number,
    state: Partial<AppState>,
    tab: number,
    firstPage: boolean,
    loc: { item: MedicalCenter | Facultativo; },
    isLastOne = false,
  ) {
    const codigoCentroMedicoMapfre = loc?.item?.codigoCentroMedicoMapfre;
    this.infoWindowHandlers[codigoCentroMedicoMapfre] = (item?: Partial<SearchInput>) => {
      const infoWindow = new google.maps.InfoWindow();
      const data: MedicalCenter | Facultativo =
        tab === 0
          ? state.medicalCentersResponse.centrosMedicos[index]
          : item
            ? item as Facultativo
            : state.medicalDoctorsResponse.facultativos[index];

      const infoWindowContent = this.getInfoWindowContent(data as MedicalCenter & Facultativo, tab, state);
      infoWindow.setContent(infoWindowContent);
      infoWindow.open(this.map, marker);
      this.resetElementsSelected(state, tab);
      this.setMedicalCenterAsSelected(state, index, tab, codigoCentroMedicoMapfre, item);
    };

    if (index === 0 && firstPage) this.infoWindowHandlers[codigoCentroMedicoMapfre]();
    if (index != 0 && isLastOne) this.ui.stopSpinner();


    marker.addListener('click', ((tempMarker, map, infoWindow) => {
      return () => {
        this.closeAllInfoWindows();
        const searchResponse = loc.item;

        if (searchResponse) {
          this.infoWindows.push(infoWindow);
          const data: MedicalCenter | Facultativo =
            tab === 0
              ? state.medicalCentersResponse.centrosMedicos[index]
              : state.medicalDoctorsResponse.facultativos.find(f => f.codigoCentroMedicoMapfre === codigoCentroMedicoMapfre);

          infoWindow.setContent(this.getInfoWindowContent(data as MedicalCenter & Facultativo, tab, state));
          infoWindow.open(map, tempMarker);
          this.resetElementsSelected(state, tab);
          this.setMedicalCenterAsSelected(state, index, tab, codigoCentroMedicoMapfre, loc.item);
        }
      };
    })(marker, this.map, this.infoWindow));
  }

  updateSelectedItems(items: MedicalCenter[] & Facultativo[]) {
    setTimeout(() => {
      const itemSelected = items.find(e => e.selected);
      if (itemSelected) {
        for (const i of items) {
          i.selected = i.codigoCentroMedicoMapfre === itemSelected.codigoCentroMedicoMapfre;
        }
      }
    });
  }

  private setMedicalCenterAsSelected(
    state: Partial<AppState>,
    index: number,
    tab: number,
    codigoCentroMedicoMapfre?: string,
    item?: Partial<SearchInput>
  ) {
    if (tab === 0) {
      const centers = state.medicalCentersResponse.centrosMedicos;
      const i = centers.findIndex(d => d.codigoCentroMedicoMapfre === codigoCentroMedicoMapfre);
      for (const d of centers) {
        d.selected = d.codigoCentroMedicoMapfre === codigoCentroMedicoMapfre;
      }
      this.scrollTo$.next(i);
    } else {
      const doctors = state.medicalDoctorsResponse.facultativos;
      let i = 0;
      if (item) {
        i = doctors.findIndex(d => d.nombreProfesionalSanitario === item.nombreProfesionalSanitario);
      }
      for (const d of doctors) {
        d.selected = d.codigoCentroMedicoMapfre === codigoCentroMedicoMapfre;
      }
      this.scrollTo$.next(i);

    }
  }

  private resetElementsSelected(state: Partial<AppState>, tab: number) {
    tab === 0
      ? state?.medicalCentersResponse?.centrosMedicos.forEach(ele => ele.selected = false)
      : state?.medicalDoctorsResponse?.facultativos.forEach(ele => ele.selected = false);
  }

  private addMapCircle(center: google.maps.LatLngLiteral) {
    this.circle?.setMap(null);
    this.circle = new google.maps.Circle({
      radius: this.currentRadio() * 1000,
      center,
      map: this.map,
      strokeColor: 'transparent',
      fillColor: '#2D373D',
      fillOpacity: 0.1,
    });
  }
}
